import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaMailBulk, FaPaperPlane } from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "./SectionTitle";
import { saveMessageContactForm } from "../../../services/contactformServices";
import { getAllSections } from "../../../services/sectionsServices";

const FormContact = ({ mode = "portail" }) => {
  const [notificationSent, setNotificationSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sections, setSections] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    section_id: "",
  });

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const allSections = await getAllSections();
        setSections(Array.isArray(allSections) ? allSections : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des associations", error);
      }
    };

    fetchSections();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setNotificationSent(false);
    setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const { name, email, message, section_id } = formData;

    try {
      setSubmitting(true);

      await saveMessageContactForm({
        name,
        email,
        message,
        section_id,
      });

      setNotificationSent(true);
      setFormData({
        name: "",
        email: "",
        message: "",
        section_id: "",
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi du message", error);
    } finally {
      setSubmitting(false);
    }
  };

  const isVitrine = mode === "vitrine";

  return (
    <section className="bg-white py-16 dark:bg-slate-950 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow={isVitrine ? "Contact" : "Informations"}
          title={isVitrine ? "Demander une " : "Contactez une "}
          highlight={isVitrine ? "présentation" : "association"}
          subtitle={
            isVitrine
              ? "Vous souhaitez présenter MonAppliAsso à une commune ou obtenir une démonstration ? Envoyez votre demande."
              : "Vous souhaitez plus d’informations sur une association ou une activité ? Envoyez votre message."
          }
        />

        <Reveal>
          <form
            className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 p-5 shadow-xl dark:border-white/10 dark:bg-slate-900 sm:p-8"
            onSubmit={handleSubmit}
          >
            <div className="grid gap-5 md:grid-cols-2">
              {!isVitrine && (
                <div className="md:col-span-2">
                  <label
                    htmlFor="section_id"
                    className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                  >
                    Association concernée
                  </label>

                  <select
                    id="section_id"
                    name="section_id"
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                    value={formData.section_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionnez une association</option>
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.nom_section}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label
                  htmlFor="name"
                  className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                >
                  Nom Prénom
                </label>

                <input
                  type="text"
                  id="name"
                  name="name"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                  placeholder="Votre nom"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                >
                  Votre email
                </label>

                <input
                  type="email"
                  id="email"
                  name="email"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                  placeholder="mail@gmail.com"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="md:col-span-2">
                <label
                  htmlFor="message"
                  className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                >
                  Votre message
                </label>

                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                  placeholder="Laissez votre message ici..."
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            <div className="mt-7 flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="flex items-center gap-3 text-sm text-slate-500 dark:text-slate-300">
                <FaMailBulk className="text-primary" />
                Votre message sera transmis au bon interlocuteur.
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3 text-sm font-extrabold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Envoi..." : "Envoyer"}
                <FaPaperPlane />
              </button>
            </div>

            {notificationSent && (
              <div className="mt-5 flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                <FaCheckCircle />
                Votre message a bien été envoyé.
              </div>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default FormContact;